/**
 * Der Hinweisstreifen am archivierten Objekt selbst (`<art>-archived-banner`,
 * Spec 02a, „Oberfläche · Archivieren") — nicht zu verwechseln mit
 * `object-archived-notice` (`features/objects`), das nach dem Archivieren auf
 * dem **Elternobjekt** steht. Dieser Streifen erscheint stattdessen, wenn man
 * das bereits archivierte Objekt selbst öffnet.
 *
 * Lag bis Spec 03 in `features/properties/`. Ab hier brauchen ihn drei
 * Fachbereiche (`properties`, `tenants`, `contracts`), und `features/*`
 * dürfen einander nicht importieren — deshalb der Umzug nach `components/`
 * (Spec 03, „Was 03 an Bestehendem ändert", Punkt 7). Rein präsentational,
 * kein Datenzugriff, deshalb ohne Anpassung übernommen.
 *
 * Seit Spec 02b ein dritter Fall: `archived_by_id` gesetzt heißt „mit diesem
 * Objekt kaskadiert archiviert" (ADR 0009 Variante B) — der Streifen nennt den
 * Namen (`archived_by_label`) und verlinkt auf das archivierende Objekt, den
 * laut ADR „einzigen verlässlichen Weg zurück". `archived_by_id` gesetzt
 * impliziert serverseitig immer `can_unarchive: false` (das Elternobjekt ist
 * dann Teil desselben archivierten Teilbaums) — die drei Fälle sind also
 * überschneidungsfrei.
 */

import { Link } from '@tanstack/react-router'

import type { ArchivedEntityKind } from '@/lib/archived-notice'

interface ArchivedBannerProps {
  readonly kind: ArchivedEntityKind
  /** Kommt aus `ObjectHead.can_unarchive` — der Client berechnet nichts
   * selbst (I9). */
  readonly canUnarchive: boolean
  /** Ziel für `object-archived-parent-link`, wenn das Elternobjekt ebenfalls
   * archiviert ist. */
  readonly parentId: string | null
  /** `ObjectHead.archived_by_id` — `null` heißt „selbst archiviert". */
  readonly archivedById: string | null
  /** `ObjectHead.archived_by_label` — nur gesetzt, wenn `archivedById` es ist. */
  readonly archivedByLabel: string | null
  readonly onUnarchive: () => void
  readonly isUnarchiving: boolean
}

export function ArchivedBanner({
  kind,
  canUnarchive,
  parentId,
  archivedById,
  archivedByLabel,
  onUnarchive,
  isUnarchiving,
}: ArchivedBannerProps) {
  return (
    <p
      data-testid={`${kind}-archived-banner`}
      role="status"
      className="border-border bg-bg text-text rounded-card flex flex-wrap items-center justify-between gap-2 border px-4 py-3"
    >
      {canUnarchive ? (
        <>
          <span>Dieses Objekt ist archiviert.</span>
          <button
            type="button"
            data-testid={`${kind}-unarchive`}
            disabled={isUnarchiving}
            aria-busy={isUnarchiving}
            onClick={onUnarchive}
            className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
          >
            Wiederherstellen
          </button>
        </>
      ) : archivedById !== null ? (
        <>
          <span>Mit „{archivedByLabel}" archiviert.</span>
          <Link
            to="/objekt/$entityId"
            params={{ entityId: archivedById }}
            search={{ tab: undefined }}
            data-testid="object-archived-root-link"
            className="text-accent hover:underline"
          >
            Objekt öffnen
          </Link>
        </>
      ) : (
        <>
          <span>Archiviert. Das übergeordnete Objekt ist ebenfalls archiviert.</span>
          {parentId !== null && (
            <Link
              to="/objekt/$entityId"
              params={{ entityId: parentId }}
              search={{ tab: undefined }}
              data-testid="object-archived-parent-link"
              className="text-accent hover:underline"
            >
              Elternobjekt öffnen
            </Link>
          )}
        </>
      )}
    </p>
  )
}
